import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ChevronLeft, ChevronRight, ExternalLink, Newspaper } from "lucide-react";
import { Widget } from "./Widget";
import { blogPosts } from "../../constants";
import type { DragHandleProps } from "../../constants";

const slideVariants = {
  enter: (d: number) => ({ x: `${d * 100}%`, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d: number) => ({ x: `${d * -100}%`, opacity: 0 }),
};

export const BlogCarouselWidget = ({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const timerRef = useRef<ReturnType<typeof setInterval>>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setDirection(1);
      setCurrent((i) => (i + 1) % blogPosts.length);
    }, 15000);
  }, []);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent((i) => (i + 1) % blogPosts.length);
    resetTimer();
  }, [resetTimer]);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent((i) => (i - 1 + blogPosts.length) % blogPosts.length);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  const post = blogPosts[current];

  return (
    <Widget
      title="Latest Posts"
      icon={<Newspaper className="size-5" aria-hidden="true" />}
      linkTo="#"
      dragHandleProps={dragHandleProps}
    >
      <div className="relative flex flex-col flex-1 min-h-0">
        <div className="relative overflow-hidden rounded-xl flex-1">
          <AnimatePresence mode="popLayout" initial={false} custom={direction}>
            <motion.article
              key={current}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="flex flex-col gap-3"
            >
              <div className="aspect-video overflow-hidden rounded-xl">
                <img src={post.image} alt={post.title} className="size-full object-cover" />
              </div>
              <div className="flex flex-col gap-1.5">
                <h4 className="text-sm font-semibold leading-snug text-neutral-900 dark:text-white line-clamp-2">
                  {post.title}
                </h4>
                <p className="text-xs leading-relaxed text-neutral-500 dark:text-neutral-400 line-clamp-2">
                  {post.description}
                </p>
                <div className="flex items-center justify-between mt-1">
                  <time className="text-xs text-neutral-400 dark:text-neutral-500">
                    {post.date}
                  </time>
                  <a
                    href="https://equal-iq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-neutral-300 px-3 py-0.5 text-xs text-neutral-600 transition-colors hover:bg-neutral-900 hover:text-white dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-white dark:hover:text-neutral-900"
                  >
                    Read more
                    <ExternalLink className="size-3" />
                  </a>
                </div>
              </div>
            </motion.article>
          </AnimatePresence>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="flex gap-1.5">
            {blogPosts.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setDirection(i > current ? 1 : -1);
                  setCurrent(i);
                  resetTimer();
                }}
                className="group p-0.5"
              >
                <motion.div
                  className="h-1.5 rounded-full"
                  animate={{
                    width: i === current ? 20 : 6,
                    backgroundColor:
                      i === current
                        ? "var(--color-neutral-800)"
                        : "var(--color-neutral-300)",
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            <button
              onClick={prev}
              className="grid size-7 place-items-center rounded-lg border border-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-white"
            >
              <ChevronLeft className="size-3.5" />
            </button>
            <button
              onClick={next}
              className="grid size-7 place-items-center rounded-lg border border-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-white"
            >
              <ChevronRight className="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </Widget>
  );
};
